import { Component, OnInit, ViewChild } from '@angular/core';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import {
  PAGE_SIZE_OPTIONS,
  PageIndexService,
} from '../../../services/page-index-service';
import { SignOverview } from '../../../model/sign/sign-overview';
import { SignsControllerRequestService } from '../../../services/api/signs-controller-request.service';
import { SnackService } from '../../../services/snack.service';
import { Router } from '@angular/router';
import { FilterEnabledComponent } from '../../../helpers/filter-enabled.component';
import { FilterStoreService } from '../../../services/filter-store.service';
import Asset from '../../../model/asset';
import { Lightbox } from 'ngx-lightbox';
import { FilterObject } from '../../../helpers/filter-object';
import { ActiveSortService } from '../../../services/active-sort.service';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import { SHORTER_DEBOUNCE_TIME } from 'src/app/services/request.service';
import { FileHelper } from '../../../helpers/file-helper';
import { SignTypesChoices } from '../../../model/sign/enums/sign-type';

@Component({
  selector: 'signs-overview',
  templateUrl: './signs-overview.component.html',
  styleUrls: ['./signs-overview.component.scss'],
})
export class SignsOverviewComponent
  extends FilterEnabledComponent
  implements OnInit
{
  loadingSigns: boolean;
  signs: SignOverview[];
  totalItems = 0;
  pageSizeOptions = PAGE_SIZE_OPTIONS;
  showOldLayouts = false;
  showLowStock = false;
  showOnFrame = false;
  showOnRoof = false;

  activeSort: string;
  activeSortAscending: boolean;
  isImage = FileHelper.isImage;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  typeOptions: { key: string; value: string }[];

  constructor(
    private signsService: SignsControllerRequestService,
    private snackService: SnackService,
    private router: Router,
    protected filterStoreService: FilterStoreService,
    protected pageIndexService: PageIndexService,
    private _lightbox: Lightbox,
    private activeSortService: ActiveSortService
  ) {
    super(
      [
        {
          key: 'type',
          value: '',
          strictEquality: true,
          startWith: true,
          active: false,
        },
        {
          key: 'title',
          active: false,
          strictEquality: false,
          numeric: false,
        },
        {
          key: 'phoneNumber',
          active: false,
          strictEquality: false,
          numeric: false,
        },
        {
          key: 'signHasParticipatingCompany__company__name',
          active: false,
          strictEquality: false,
          numeric: false,
        },
        {
          key: 'defaultLocation__rack',
          active: false,
          strictEquality: true,
          numeric: false,
        },
      ],
      filterStoreService,
      pageIndexService
    );
  }

  ngOnInit(): void {
    this.initSortSettings();
    this.initPageSettings();
    this.initMainFilters();

    this.fetchSigns();
    this.addSubscription(
      this.filtersChanged,
      () => {
        this.pageIndexService.setPageIndex(this.getLocalStorageKey(), 0);
        this.paginator.pageIndex = 0;
        this.fetchSigns();
      },
      GeneralHelper.noop,
      SHORTER_DEBOUNCE_TIME
    );

    this.initTypeOptions();
  }

  private readonly SHOW_OLD_LAYOUTS_FILTER = 'showOldLayouts';
  private readonly SHOW_LOW_STOCK_FILTER = 'showLowStock';
  private readonly SHOW_ON_FRAME_FILTER = 'showOnFrame';
  private readonly SHOW_ON_ROOF_FILTER = 'showOnRoof';

  toggleOldLayouts() {
    this.showOldLayouts = !this.showOldLayouts;
    this.storeMainFilter(this.SHOW_OLD_LAYOUTS_FILTER, this.showOldLayouts);
    this.fetchSigns();
  }

  toggleLowStock() {
    this.showLowStock = !this.showLowStock;
    this.storeMainFilter(this.SHOW_LOW_STOCK_FILTER, this.showLowStock);
    this.fetchSigns();
  }

  toggleOnFrame() {
    this.showOnFrame = !this.showOnFrame;
    this.storeMainFilter(this.SHOW_ON_FRAME_FILTER, this.showOnFrame);
    this.fetchSigns();
  }

  toggleOnRoof() {
    this.showOnRoof = !this.showOnRoof;
    this.storeMainFilter(this.SHOW_ON_ROOF_FILTER, this.showOnRoof);
    this.fetchSigns();
  }

  private initSortSettings() {
    const activeSort = this.activeSortService.getActiveSort(
      this.getLocalStorageKey()
    );
    const activeSortAscending = this.activeSortService.isActiveSortAscending(
      this.getLocalStorageKey()
    );

    this.activeSort = activeSort != null ? activeSort : this.getDefaultSort();
    this.activeSortAscending =
      activeSortAscending != null ? activeSortAscending : true;
  }

  private initPageSettings() {
    const pageIndex = this.pageIndexService.getPageIndex(
      this.getLocalStorageKey()
    );
    const pageSize = this.pageIndexService.getPageSize(
      this.getLocalStorageKey()
    );

    this.paginator.pageIndex = pageIndex != null ? pageIndex : 0;
    this.paginator.pageSize = pageSize != null ? pageSize : 30;
  }

  private initTypeOptions() {
    this.typeOptions = Object.keys(SignTypesChoices).map((signTypeName) => ({
      key: signTypeName,
      value: SignTypesChoices[signTypeName].label,
    }));
  }

  private initMainFilters() {
    let key = this.getActiveMainFiltersKey();

    let activeMainFilters: string[] = JSON.parse(localStorage.getItem(key)!);
    activeMainFilters?.forEach((value) => {
      switch (value) {
        case this.SHOW_OLD_LAYOUTS_FILTER:
          this.showOldLayouts = true;
          break;
        case this.SHOW_LOW_STOCK_FILTER:
          this.showLowStock = true;
          break;
        case this.SHOW_ON_FRAME_FILTER:
          this.showOnFrame = true;
          break;
        case this.SHOW_ON_ROOF_FILTER:
          this.showOnRoof = true;
          break;
      }
    });
  }

  protected getLocalStorageKey(): string {
    return 'SignsOverviewComponent';
  }

  private fetchSigns(): void {
    this.loadingSigns = true;
    this.signs = [];
    this.signsService
      .getPaged(
        this.getFilterString(),
        this.activeSort,
        this.activeSortAscending,
        this.paginator.pageSize,
        this.paginator.pageIndex,
        false
      )
      .then((res) => {
        this.signs = res.items;
        this.totalItems = res.totalItems;
      })
      .catch(() => {
        this.snackService.showError(
          'Ophalen borden mislukt. Herlaad de pagina'
        );
      })
      .finally(() => {
        this.loadingSigns = false;
      });
  }

  getFilterString(): string {
    const oldLayout = this.showOldLayouts
      ? 'oldLayout:true'
      : 'oldLayout:false';
    const lowStock = this.showLowStock ? 'lowStock:true' : 'lowStock:false';
    const onFrame = this.showOnFrame ? 'onFrame:true' : 'onFrame:false';
    const onRoof = this.showOnRoof
      ? 'physicalStockOnRoof:true'
      : 'physicalStockOnRoof:false';

    const filterString = this.generateFilterString();
    const baseElements = [oldLayout, lowStock, onFrame, onRoof];

    return filterString
      ? [filterString, ...baseElements].join(',')
      : baseElements.join(',');
  }

  addSign(): void {
    this.router.navigateByUrl('/signs-portal/signs/-1');
  }

  signDetail(sign: SignOverview) {
    this.router.navigateByUrl(`/signs-portal/signs/${sign.id}`);
  }

  activateSort(sort: string) {
    if (this.activeSort === sort) {
      if (!this.activeSortAscending) {
        this.resetSort();
      } else {
        this.activeSortAscending = !this.activeSortAscending;
      }
    } else {
      this.activeSort = sort;
    }
    this.pageIndexService.setPageIndex(this.getLocalStorageKey(), 0);
    this.paginator.pageIndex = 0;

    this.activeSortService.setActiveSort(
      this.getLocalStorageKey(),
      this.activeSort
    );
    this.activeSortService.setActiveSortAscending(
      this.getLocalStorageKey(),
      this.activeSortAscending
    );

    this.fetchSigns();
  }

  private resetSort(): void {
    this.activeSort = this.getDefaultSort();
    this.activeSortAscending = true;
  }

  openLightbox(asset: Asset): void {
    let albums = [];

    const src = '/api/assets/' + asset.uuid;
    const caption = 'Design';
    const thumb = '/api/assets/' + asset.uuid;
    const album = {
      src: src,
      caption: caption,
      thumb: thumb,
    };

    albums.push(album);
    this._lightbox.open(albums, 0);
  }

  public getFilterForKey(key: string): FilterObject {
    return super.getFilterForKey(key);
  }

  public toggleFilter(filterName: string): void {
    super.toggleFilter(filterName);
  }

  public onFilterValueChange(filterData: any): void {
    super.onFilterValueChange(filterData);
  }

  onPageChange() {
    this.pageIndexService.setPageIndex(
      this.getLocalStorageKey(),
      this.paginator.pageIndex
    );
    this.pageIndexService.setPageSize(
      this.getLocalStorageKey(),
      this.paginator.pageSize
    );

    this.fetchSigns();
  }

  public getDefaultSort() {
    return 'companyRegularName';
  }

  public storeMainFilter(mainFilter: string, active: boolean) {
    let key = this.getActiveMainFiltersKey();
    let activeMainFilters: string[] = JSON.parse(localStorage.getItem(key));
    let result: string[];
    if (active) {
      result =
        activeMainFilters !== null
          ? activeMainFilters.concat(mainFilter)
          : [mainFilter];
    } else {
      result =
        activeMainFilters !== null
          ? activeMainFilters.filter((value) => value !== mainFilter)
          : [];
    }
    localStorage.setItem(key, JSON.stringify(result));
  }

  private getActiveMainFiltersKey() {
    return this.getLocalStorageKey() + '-activeMainFilters';
  }
}
